
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { User } from '../types';
import i18n from '../i18n/index';
import { getPublicProfile, searchPublicProfiles, PublicProfile } from './publicProfileService';

/**
 * Public user profile info (minimal data for display)
 */
export interface UserPublicProfile {
  uid: string;
  displayName: string;
  photoURL: string;
  username?: string;
}

// In-memory cache to reduce Firestore reads
const userCache = new Map<string, UserPublicProfile>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const cacheTimestamps = new Map<string, number>();

/**
 * Get public profile information for a user
 * CRITICAL: Reads from /publicProfiles, NOT /users
 * Uses in-memory caching to reduce Firestore reads
 */
export const getUserPublicProfile = async (uid: string): Promise<UserPublicProfile> => {
  // Check cache first
  const cached = userCache.get(uid);
  const cacheTime = cacheTimestamps.get(uid);
  
  if (cached && cacheTime && Date.now() - cacheTime < CACHE_TTL) {
    return cached;
  }

  try {
    // CRITICAL: Read from /publicProfiles, not /users
    const publicProfile = await getPublicProfile(uid);

    if (!publicProfile) {
      // Return fallback profile
      const fallback: UserPublicProfile = {
        uid,
        displayName: i18n.t('common.user_default'),
        photoURL: `https://api.dicebear.com/7.x/avataaars/svg?seed=${uid}`,
        username: undefined
      };
      userCache.set(uid, fallback);
      cacheTimestamps.set(uid, Date.now());
      return fallback;
    }

    const profile: UserPublicProfile = {
      uid: publicProfile.userId,
      displayName: publicProfile.name || i18n.t('common.user_default'),
      photoURL: publicProfile.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${uid}`,
      username: undefined // Username not available in PublicProfile
    };

    // Update cache
    userCache.set(uid, profile);
    cacheTimestamps.set(uid, Date.now());
    
    return profile;
  } catch (error: any) {
    if (import.meta.env.DEV) {
      console.error('[USER] Error fetching public profile:', error);
    }
    // Return fallback on error
    const fallback: UserPublicProfile = {
      uid,
      displayName: 'User',
      photoURL: `https://api.dicebear.com/7.x/avataaars/svg?seed=${uid}`,
      username: undefined
    };
    return fallback;
  }
};

/**
 * Clear cache for a specific user (useful when user updates their profile)
 */
export const clearUserCache = (uid: string): void => {
  userCache.delete(uid);
  cacheTimestamps.delete(uid);
};

/**
 * Clear all user cache
 */
export const clearAllUserCache = (): void => {
  userCache.clear();
  cacheTimestamps.clear();
};

/**
 * Search users by name
 * CRITICAL: Uses /publicProfiles, NOT /users
 * Returns up to 20 matching users (excluding the current user)
 */
export const searchUsersByName = async (
  searchTerm: string,
  excludeUid?: string
): Promise<UserPublicProfile[]> => {
  if (!searchTerm.trim()) {
    return [];
  }

  try {
    if (import.meta.env.DEV) {
      console.log('[USER_SEARCH] Starting search for:', searchTerm, 'excluding:', excludeUid);
    }
    
    // CRITICAL: Use searchPublicProfiles from publicProfileService
    // This reads from /publicProfiles, not /users
    const publicProfiles = await searchPublicProfiles(searchTerm, excludeUid);
    
    // Convert PublicProfile[] to UserPublicProfile[]
    const results: UserPublicProfile[] = publicProfiles.map(profile => ({
      uid: profile.userId,
      displayName: profile.name || i18n.t('common.user_default'),
      photoURL: profile.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile.userId}`,
      username: undefined // Username not available in PublicProfile
    }));
    
    if (import.meta.env.DEV) {
      console.log('[USER_SEARCH] ✅ Search complete:', {
        searchTerm,
        resultsFound: results.length,
        excludeUid,
        results: results.map(u => ({ uid: u.uid, displayName: u.displayName }))
      });
    }
    
    return results;
  } catch (error: any) {
    if (import.meta.env.DEV) {
      console.error('[USER_SEARCH] ❌ CRITICAL: Error searching users:', {
        error: error,
        code: error.code,
        message: error.message,
        searchTerm,
        stack: error.stack
      });
    }
    return [];
  }
};

